import {
  GET_METRICS_BY_IDS,
  governanceApiRef,
} from '@agilelab/plugin-wb-governance';
import {
  MetricByIdQuery,
  ThresholdResult as ThresholdResultType,
} from '@agilelab/plugin-wb-governance-common';
import {
  GenericList,
  GenericListItem,
  ThresholdResult,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Typography } from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import { ReactNode } from 'react';
import useAsync from 'react-use/lib/useAsync';

const statusIconStyle = {
  marginRight: '8px',
  display: 'flex',
  alignItems: 'center',
};

interface MetricItem {
  metricName: string;
  metricVersion: string;
  metricDescription: string;
  result?: ThresholdResultType | null;
  value?: number;
  error: boolean;
}

export function MetricResultsOverview({
  title,
  hideIfEmpty = false,
  noDataMessage,
  metricIdFilter,
  resource,
  wrapper,
}: {
  title?: string;
  hideIfEmpty?: boolean;
  noDataMessage?: string;
  metricIdFilter: string[];
  resource: { externalId: string; environment: string };
  wrapper?: (children: ReactNode) => JSX.Element;
}): JSX.Element {
  const identityApi = useApi(identityApiRef);
  const governanceApi = useApi(governanceApiRef);
  const apolloClient = useApolloClient();

  const {
    loading,
    value: metricItems,
    error,
  } = useAsync(async () => {
    if (!metricIdFilter.length) return [];

    const { results } = await governanceApi.getMetricResults(
      {
        resource: resource.externalId,
        environment: resource.environment,
        metrics: metricIdFilter,
      },
      await identityApi.getCredentials(),
    );

    if (!results.length) return [];

    const uniqueMetricIds = [...new Set(results.map(r => r.metricId))];

    const { data } = await apolloClient.query<MetricByIdQuery>({
      query: GET_METRICS_BY_IDS,
      variables: { metricIds: uniqueMetricIds },
    });

    const metricsMap = new Map(
      data.cgp_governance_entity.map(metric => [
        metric.governance_entity_id,
        metric,
      ]),
    );

    return results
      .map(result => {
        const metric = metricsMap.get(result.metricId);

        if (!metric) {
          return undefined;
        }

        return {
          metricName: metric.name,
          metricVersion: `v:${metric.version}`,
          metricDescription: metric.description,
          result: result.evaluationResult.thresholdResult,
          value: result.evaluationResult.value,
          error: !!result.evaluationResult.errors?.length,
        };
      })
      .filter(Boolean) as MetricItem[];
  }, [
    governanceApi,
    identityApi,
    apolloClient,
    resource.externalId,
    resource.environment,
    metricIdFilter,
  ]);

  const items: GenericListItem[] | undefined = metricItems?.map(v => ({
    icon: (
      <ThresholdResult
        value={v.value ?? 0}
        result={v.result}
        hasError={v.error}
        variant="reduced"
        style={statusIconStyle}
      />
    ),
    text: (
      <WbTooltip title={v.metricDescription ?? v.metricName}>
        <Typography>{`${v.metricName} ${v.metricVersion}`}</Typography>
      </WbTooltip>
    ),
  }));

  if (hideIfEmpty && (loading || (items && !items.length))) return <></>;

  const list = (
    <GenericList
      label={title}
      isLoading={loading}
      isError={!!error}
      error={
        error && (
          <Typography>Metric results are temporarily unavailable</Typography>
        )
      }
      items={items}
      noData={<Typography>{noDataMessage ?? 'No results'}</Typography>}
    />
  );

  return wrapper ? wrapper(list) : list;
}
